import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { canDrop, moveParams } from "../utils/fleet_montage"

// Montage view of the Fuhrpark (docs/plans/fleet-attachments.md, A3b): drag an Anbaugerät or Werkzeug
// chip onto a carrier card to mount / hand it over, or onto the pool to take it off.
//
// ONE delegated controller on the view, like planning-board-drop: the cards and chips are replaced by
// Turbo Streams after every move, so they carry only data attributes and no listeners of their own.
// Native HTML5 drag and drop (desktop); on touch devices the chip's "abnehmen" button and the card's
// "+" buttons do the same by click. The server's Turbo Stream repaints the cards, the pool and the flash.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this.listeners = {
      dragstart: this.handleDragStart.bind(this),
      dragend: this.handleDragEnd.bind(this),
      dragover: this.handleDragOver.bind(this),
      dragleave: this.handleDragLeave.bind(this),
      drop: this.handleDrop.bind(this),
    }
    Object.entries(this.listeners).forEach(([name, fn]) => this.element.addEventListener(name, fn))
  }

  disconnect() {
    Object.entries(this.listeners).forEach(([name, fn]) =>
      this.element.removeEventListener(name, fn),
    )
  }

  handleDragStart(event) {
    const chip = event.target.closest?.("[data-montage-item]")
    if (!chip) return

    this.dragging = {
      item: chip.dataset.montageItem,
      kind: chip.dataset.montageKind,
      origin: chip.dataset.montageOrigin || "",
    }
    this.chip = chip
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.dragging.item)
    chip.classList.add("opacity-50")
  }

  handleDragEnd() {
    this.chip?.classList.remove("opacity-50")
    this.clearHighlight()
    this.dragging = null
    this.chip = null
  }

  handleDragOver(event) {
    const zone = this.zoneFor(event)
    if (!zone || !canDrop(this.dragging, zone.dataset)) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    this.highlight(zone)
  }

  handleDragLeave(event) {
    const zone = this.zoneFor(event)
    if (zone && !zone.contains(event.relatedTarget)) this.clearHighlight()
  }

  handleDrop(event) {
    const zone = this.zoneFor(event)
    if (!zone || !canDrop(this.dragging, zone.dataset)) return

    event.preventDefault()
    const params = moveParams(this.dragging, zone.dataset)
    this.handleDragEnd()
    this.move(params)
  }

  zoneFor(event) {
    const zone = event.target.closest?.("[data-montage-target-id]")
    return zone && this.element.contains(zone) ? zone : null
  }

  highlight(zone) {
    if (this.highlighted === zone) return
    this.clearHighlight()
    zone.classList.add("ring-2", "ring-green-500", "border-green-500")
    this.highlighted = zone
  }

  clearHighlight() {
    this.highlighted?.classList.remove("ring-2", "ring-green-500", "border-green-500")
    this.highlighted = null
  }

  // The server answers a refusal with a 422 flash stream, so any Turbo Stream is rendered as is.
  async move(params) {
    const body = new FormData()
    Object.entries(params).forEach(([key, value]) => body.append(key, value))
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": document.head.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "text/vnd.turbo-stream.html",
        },
        body,
      })
      if ((response.headers.get("Content-Type") || "").includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(await response.text())
      }
    } catch (error) {
      Honeybadger.notify(error, { context: { component: "fleet-montage" } })
    }
  }
}
